import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { some } from 'lodash-es'
import { beamLikeGuidsForCalcPositionChecks } from '@editor/pages/Results2/components/Results2DetailPane/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useTransmissionStore } from '@editorStores'
import { useElementTypes, useResultsInvalidation } from '@editorHooks'
import { getElementChecksBundle, postCalcPositionChecks } from '@queries'

/**
 * Recomputes the checks of a member (and its beam-like supports) after its cross section changed,
 * like the results view does on a cross section change. Only runs when the member already has
 * computed checks — otherwise there is nothing to keep up to date. Runs as its own mutation in the
 * background, so the cross section save does not wait for it; the results are invalidated once it
 * settled, so they are refetched with the new checks rather than the stale ones.
 *
 * Whether the member has checks is read from the element check bundle (cached if the results were
 * loaded already), not from the results store, which is only filled on the results pages.
 */
const useRecomputeMemberChecks = () => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const elementTypeLookup = useElementTypes()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const invalidateResultsQueries = useResultsInvalidation()

  const { mutate } = useMutation({
    mutationKey: [projectId, 'recomputeMemberChecksAfterCsChange'],
    mutationFn: async (elementGuid: string): Promise<boolean> => {
      const bundle: ElementCheckBundle | null = await queryClient.fetchQuery({
        queryKey: getElementChecksBundle.getKey(projectId),
        queryFn: ({ signal }) => getElementChecksBundle.request(projectId, signal),
      })
      if (!some(bundle?.structural_checks, { element_guid: elementGuid })) return false

      const beamLike = beamLikeGuidsForCalcPositionChecks({
        elementId: elementGuid,
        verticalTransmissionGraph,
        elementTypeLookup,
      })
      if (beamLike.length === 0) return false

      await postCalcPositionChecks.request(projectId, beamLike)
      return true
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Neuberechnen der Nachweise'), {
        variant: 'error',
      })
    },
    onSettled: () => invalidateResultsQueries(projectId as string),
  })

  return mutate
}

export default useRecomputeMemberChecks
